import {
  Box,
  Button,
  Drawer,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useGetBranches } from "src/api/generic";
import FilterIcon from "src/assets/svg-icons/aggregators/filter.svg";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useGetAccountTree } from "src/api/tree/tree";
import AccountTreeDropdown, {
  TreeNode,
} from "src/shared/components/account-tree-dropdown";

interface IProps {
  open: boolean;
  handleClose: () => void;
  journalId: string;
  setJournalId: (v: string) => void;
  source: string;
  setSource: (v: string) => void;
  reference: string;
  setReference: (v: string) => void;
  accountNode: TreeNode | null;
  setAccountNode: (v: TreeNode | null) => void;
  branchId: string;
  setBranchId: (v: string) => void;
}

const SOURCES = [
  { value: "opening_balance", labelKey: "openingBalance" },
  { value: "purchasing", labelKey: "purchasing" },
  { value: "operation_expenses", labelKey: "operationExpenses" },
  { value: "fixed_assets", labelKey: "fixedAssets" },
  { value: "payment", labelKey: "payments" },
  { value: "inventory", labelKey: "inventory" },
  { value: "sales", labelKey: "sales" },
  { value: "event", labelKey: "event" },
  { value: "manual_journal", labelKey: "manualJournals" },
];

const FilterDrawer = (props: IProps) => {
  const {
    open,
    handleClose,
    journalId,
    setJournalId,
    source,
    setSource,
    reference,
    setReference,

    branchId,
    setBranchId,
    accountNode,
    setAccountNode,
  } = props;

  const { t } = useTranslation("purchasing");
  const { t: generalT } = useTranslation("general");

  const [tempJournalId, setTempJournalId] = useState(journalId);
  const [tempSource, setTempSource] = useState(source);
  const [tempReference, setTempReference] = useState(reference);
  const [tempAccountNode, setTempAccountNode] = useState<TreeNode | null>(
    accountNode,
  );
  const [tempBranchId, setTempBranchId] = useState(branchId);

  const { data: branchesData } = useGetBranches();
  const { data: treeData, isLoading: isTreeLoading } = useGetAccountTree();

  const sourceLabels: Record<string, string> = {
    openingBalance: generalT("startBalance"),
    purchasing: generalT("purchasing"),
    operationExpenses: generalT("operationExpenses"),
    fixedAssets: generalT("fixedAssets"),
    payments: generalT("payments"),
    inventory: generalT("inventory"),
    sales: generalT("sales"),
    event: generalT("events"),
    manualJournals: t("manualJournals"),
  };

  const handleApply = () => {
    handleClose();
    setJournalId(tempJournalId);
    setSource(tempSource);
    setReference(tempReference);
    setAccountNode(tempAccountNode);
    setBranchId(tempBranchId);
  };

  const handleClearFilters = () => {
    handleClose();
    setJournalId("");
    setSource("");
    setReference("");
    setAccountNode(null);
    setBranchId("");

    setTempJournalId("");
    setTempSource("");
    setTempReference("");
    setTempAccountNode(null);
    setTempBranchId("");
  };

  return (
    <Drawer anchor={"right"} open={open} onClose={handleClose}>
      <Stack
        maxWidth={{ xs: "100%", sm: "376px" }}
        minWidth={{ xs: "100%", sm: "376px" }}
        borderRight={"1px solid #D0D5DD"}
        height={"100%"}
        maxHeight={"100vh"}
        top={"0"}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          p={"20px 27px"}
          bgcolor={"#fff"}
          boxShadow="0px 0px 40px 0px rgba(11, 24, 42, 0.07)"
        >
          <Button
            color="tertiary"
            variant="text"
            size="small"
            endIcon={<img src={FilterIcon} height={"16px"} alt="" />}
          >
            {t("filter")}
          </Button>
          <Button
            variant="outlined"
            color="tertiary"
            size="small"
            onClick={handleClose}
          >
            {generalT("hide")}
          </Button>
        </Box>
        <Stack spacing={"20px"} p={"40px 27px"}>
          {/* Journal ID */}
          <TextField
            label={t("journalId")}
            variant="outlined"
            size="small"
            fullWidth
            value={tempJournalId}
            onChange={(e) => setTempJournalId(e.target.value)}
          />
          {/* Source */}
          <FormControl fullWidth size="small">
            <InputLabel>{t("source")}</InputLabel>
            <Select
              value={tempSource}
              onChange={(e) => setTempSource(e.target.value)}
              input={<OutlinedInput label={t("source")} />}
            >
              <MenuItem value="">
                <ListItemText primary={generalT("all")} />
              </MenuItem>
              {SOURCES.map(({ value, labelKey }) => (
                <MenuItem key={value} value={value}>
                  <ListItemText primary={sourceLabels[labelKey] || labelKey} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Reference */}
          <TextField
            label={generalT("reference")}
            variant="outlined"
            size="small"
            fullWidth
            value={tempReference}
            onChange={(e) => setTempReference(e.target.value)}
          />
          {/* Account (tree dropdown) */}
          <AccountTreeDropdown
            fullWidth
            treeData={treeData?.data || []}
            isLoading={isTreeLoading}
            onSelect={(node) => {
              setTempAccountNode(node);
            }}
            label={generalT("account")}
            size="small"
            variant="textfield"
            value={tempAccountNode}
            onClear={() => {
              setTempAccountNode(null);
            }}
            allowParentSelect
          />
          {/* Branch */}
          <FormControl fullWidth size="small">
            <InputLabel>{generalT("branch")}</InputLabel>
            <Select
              value={tempBranchId}
              onChange={(e) => setTempBranchId(e.target.value)}
              input={<OutlinedInput label={generalT("branch")} />}
            >
              {branchesData?.data?.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  <ListItemText primary={branch.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* divider */}
          <Box width={"100%"} height={"1px"} bgcolor={"#CBD5E1"} />
        </Stack>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          p={"20px 27px"}
          bgcolor={"#fff"}
          boxShadow="0px 0px 40px 0px rgba(11, 24, 42, 0.07)"
          alignItems={"flex-end"}
          mt="auto"
        >
          <Button variant="contained" size="small" onClick={handleApply}>
            {generalT("apply")}
          </Button>
          <Button
            variant="outlined"
            color="tertiary"
            size="small"
            onClick={handleClearFilters}
          >
            {generalT("clear")}
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default FilterDrawer;
